import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { StatusBadge } from '../components/payment-plans/StatusBadge';
import { api as ppApi } from '../components/payment-plans/utils';
import {
  Users,
  DollarSign,
  Calendar,
  CheckCircle,
  TrendingUp,
  Edit,
  Trash2,
  Clock,
  RefreshCw,
  Sparkles,
  Wifi,
  WifiOff,
  Filter,
  Receipt,
  Download,
} from 'lucide-react';

export default function NewAssessmentsPage() {
  const [metrics, setMetrics] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [halaxyConnected, setHalaxyConnected] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStage, setSyncStage] = useState('');
  const [dateNotSet, setDateNotSet] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('all_time');
  const [auditData, setAuditData] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [planBadges, setPlanBadges] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    age: 'Adult',
    email: '',
    assessment_type: 'ASD',
    intake_paperwork: 'No',
    mhcp: 'No',
    payment_type: 'Private',
    total_cost: '',
    total_paid: '',
    process: 'Intake Sent',
    process_date: '',
    date_of_next_session: '',
    notes: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      window.location.href = '/admin-login';
      return;
    }
    fetchData();
    checkHalaxyStatus();
    resumeRunningSync();
  }, []);

  const resumeRunningSync = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/halaxy/sync/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const job = await res.json();
      if (job.status === 'running') {
        setIsSyncing(true);
        setSyncStage(job.stage || '');
        pollSyncStatus(job.job_id);
      }
    } catch (e) {
      // ignore
    }
  };

  const checkHalaxyStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/halaxy/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setHalaxyConnected(data.connected);
      }
    } catch (error) {
      setHalaxyConnected(false);
    }
  };

  const fetchData = async (period = selectedPeriod) => {
    const token = localStorage.getItem('adminToken');
    try {
      const metricsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/metrics${period ? `?period=${period}` : ''}`;
      const [metricsRes, assessmentsRes] = await Promise.all([
        fetch(metricsUrl, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (metricsRes.ok) setMetrics(await metricsRes.json());
      if (assessmentsRes.ok) setAssessments(await assessmentsRes.json());
      ppApi('/badges').then(setPlanBadges).catch(() => {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const pollSyncStatus = (jobId) => {
    const token = localStorage.getItem('adminToken');
    const started = Date.now();
    const timer = setInterval(async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/halaxy/sync/status${jobId ? `?job_id=${jobId}` : ''}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const job = await res.json();
        setSyncStage(job.stage || '');

        if (job.status === 'completed') {
          clearInterval(timer);
          setIsSyncing(false);
          setSyncStage('');
          const r = job.result || {};
          toast.success('Halaxy sync complete', {
            description: `Updated ${r.assessments_updated || 0} clients. Synced ${r.appointments_synced || 0} appointments, ${r.invoices_synced || 0} invoices.`,
          });
          fetchData();
        } else if (job.status === 'failed') {
          clearInterval(timer);
          setIsSyncing(false);
          setSyncStage('');
          toast.error('Sync failed', { description: job.error || 'Check your Halaxy connection.' });
        } else if (Date.now() - started > 20 * 60 * 1000) {
          clearInterval(timer);
          setIsSyncing(false);
          setSyncStage('');
          toast.error('Sync is taking too long', { description: 'Please try again shortly.' });
        }
      } catch (e) {
        // transient network error — keep polling
      }
    }, 3000);
  };

  const handleSyncHalaxy = async () => {
    if (!halaxyConnected) {
      toast.error('Halaxy not connected', {
        description: 'Please configure your Halaxy API credentials in Settings.',
      });
      return;
    }
    setIsSyncing(true);
    setSyncStage('Starting sync…');
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/halaxy/sync`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (!data.started) {
          toast.info('A sync is already running', { description: 'Watching its progress…' });
        }
        pollSyncStatus(data.job_id);
      } else {
        setIsSyncing(false);
        setSyncStage('');
        toast.error('Sync failed to start');
      }
    } catch (error) {
      setIsSyncing(false);
      setSyncStage('');
      toast.error('Sync failed', { description: 'Check your Halaxy connection.' });
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setSelectedPeriod(newPeriod);
    fetchData(newPeriod);
  };

  const runAudit = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments/audit`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAuditData(data);
        setShowAudit(true);
      } else {
        toast.error('Audit failed');
      }
    } catch (error) {
      toast.error('Audit failed');
    }
  };

  const downloadContacts = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/export`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contacts_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Contacts exported successfully');
      } else {
        toast.error('Export failed');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };




  const handleOpenDialog = (assessment = null) => {
    if (assessment) {
      setEditingAssessment(assessment);
      const hasDate = assessment.date_of_next_session && assessment.date_of_next_session !== null;
      setDateNotSet(!hasDate);
      setFormData({
        name: assessment.name,
        age: assessment.age,
        email: assessment.email,
        assessment_type: assessment.assessment_type,
        intake_paperwork: assessment.intake_paperwork,
        mhcp: assessment.mhcp || 'No',
        payment_type: assessment.payment_type,
        total_cost: assessment.total_cost.toString(),
        total_paid: assessment.total_paid.toString(),
        process: assessment.process,
        process_date: assessment.process_date ? assessment.process_date.split('T')[0] : '',
        date_of_next_session: hasDate ? assessment.date_of_next_session.split('T')[0] : '',
        notes: assessment.notes || '',
      });
    } else {
      setEditingAssessment(null);
      setDateNotSet(false);
      setFormData({
        name: '',
        age: 'Adult',
        email: '',
        assessment_type: 'ASD',
        intake_paperwork: 'No',
        mhcp: 'No',
        payment_type: 'Private',
        total_cost: '',
        total_paid: '',
        process: 'Intake Sent',
        process_date: '',
        date_of_next_session: '',
        notes: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const token = localStorage.getItem('adminToken');

    try {
      const payload = {
        ...formData,
        total_cost: parseFloat(formData.total_cost) || 0,
        total_paid: parseFloat(formData.total_paid) || 0,
        process_date: formData.process_date ? `${formData.process_date}T00:00:00Z` : null,
        date_of_next_session: dateNotSet ? null : (formData.date_of_next_session ? `${formData.date_of_next_session}T00:00:00Z` : null),
        notes: formData.notes || null,
      };

      const url = editingAssessment
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments/${editingAssessment.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments`;

      const method = editingAssessment ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingAssessment ? 'Assessment updated!' : 'Assessment created!');
        setIsDialogOpen(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to save assessment', {
          description: error.detail || 'Please try again',
        });
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Failed to save assessment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/assessments/${id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        toast.success('Assessment deleted');
        fetchData();
      } else {
        toast.error('Failed to delete assessment');
      }
    } catch (error) {
      toast.error('Failed to delete assessment');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return 'Invalid date'; }
  };

  const getStatusBadgeColor = (process) => {
    const colors = {
      'Intake Sent': 'bg-[#F5F1E8] text-[#6B6560]',
      'Intake Received': 'bg-[#E8E4DC] text-[#3A3530]',
      'Background Booked': 'bg-[#D4E4CE] text-[#3A5A2E]',
      'In Progress': 'bg-[#8B9D83]/20 text-[#5A6F52] border border-[#8B9D83]/30',
      'Report Writing': 'bg-[#D4A574]/20 text-[#8B6B4A] border border-[#D4A574]/30',
      'Report Completed': 'bg-[#7BA05B]/20 text-[#4A6B2E] border border-[#7BA05B]/30',
      'Feedback Session': 'bg-[#6B8FA3]/20 text-[#3A5A6B] border border-[#6B8FA3]/30',
      'Completed': 'bg-[#A8B9A0] text-[#3A3530] hover:bg-[#8B9D83]',
      'Disengaged': 'bg-gray-200 text-gray-600',
    };
    return colors[process] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusIcon = (assessment) => {
    if (assessment.total_cost === 0) return <span className="text-[#9B9590]">-</span>;
    if (assessment.total_paid >= assessment.total_cost) return <CheckCircle className="h-5 w-5 text-[#7BA05B] mx-auto" />;
    if (assessment.total_paid > 0) return <span className="text-[#D4A574] text-xl">&#x26A0;</span>;
    return <span className="text-[#C17B6B] text-xl font-bold">&#x2717;</span>;
  };

  const calculateNextStep = (process) => {
    const steps = {
      'Intake Sent': 'Await intake return',
      'Intake Received': 'Schedule background',
      'Background Booked': 'Complete background session',
      'In Progress': 'Continue assessment sessions',
      'Report Writing': 'Complete report',
      'Report Completed': 'Schedule feedback',
      'Feedback Session': 'Complete feedback',
      'Completed': 'Assessment complete',
      'Disengaged': 'Follow up or close',
    };
    return steps[process] || 'Unknown';
  };

  const activeAssessments = assessments.filter((a) =>
    !['Completed', 'Disengaged'].includes(a.process)
  ).sort((a, b) => {
    // Use halaxy_next_appointment when available, fall back to date_of_next_session
    const aDate = a.halaxy_next_appointment || a.date_of_next_session;
    const bDate = b.halaxy_next_appointment || b.date_of_next_session;
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return new Date(aDate) - new Date(bDate);
  });

  const completedAssessments = assessments.filter((a) => a.process === 'Completed');
  const disengagedAssessments = assessments.filter((a) => a.process === 'Disengaged');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#8B9D83] border-r-transparent"></div>
          <p className="mt-4 text-[#6B6560]">Loading assessments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-8" data-testid="new-assessments-page">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>New Assessments - Navigating Minds</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl lg:text-4xl font-semibold text-[#3A3530] font-heading" data-testid="new-assessments-title">
              New Assessments
            </h1>
            <p className="mt-2 text-[#6B6560]">
              Auto-synced with Halaxy — add new clients from the <a href="/admin/dashboard" className="text-[#8B9D83] underline hover:text-[#6B7D63]">Assessments</a> page
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4 sm:mt-0">
            {/* Halaxy status indicator */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
              halaxyConnected 
                ? 'bg-[#D4E4CE] text-[#3A5A2E]' 
                : 'bg-[#F5F1E8] text-[#9B9590]'
            }`} data-testid="halaxy-status">
              {halaxyConnected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
              {halaxyConnected ? 'Halaxy Connected' : 'Halaxy Not Connected'}
            </div>
            <Button
              onClick={handleSyncHalaxy}
              disabled={isSyncing || !halaxyConnected}
              variant="outline"
              className="border-[#8B9D83] text-[#6B7D63] hover:bg-[#8B9D83] hover:text-white disabled:opacity-50"
              data-testid="sync-halaxy-btn"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync Halaxy'}
            </Button>
            {isSyncing && syncStage && (
              <span className="text-xs text-[#6B7D63]" data-testid="sync-stage-label">{syncStage}</span>
            )}
          </div>
        </div>

        {/* Metrics Cards */}
        {metrics && (
          <div className="space-y-4 mb-8">
            {/* Revenue Cards — Pipeline vs Invoiced */}
            <Card className="bg-white border-[#E8E4DC]" data-testid="gross-revenue-card">
              <CardContent className="pt-5 pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="h-12 w-12 rounded-xl bg-[#8B9D83]/10 flex items-center justify-center">
                      <Receipt className="h-6 w-6 text-[#8B9D83]" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#9B9590] uppercase tracking-wide">Pipeline (Quoted)</p>
                      <p className="text-2xl font-bold text-[#3A3530]" data-testid="metric-pipeline-gross">
                        {formatCurrency(metrics.filtered_gross !== null && metrics.filtered_gross !== undefined ? metrics.filtered_gross : metrics.total_gross)}
                      </p>
                    </div>
                    <div className="border-l border-[#E8E4DC] pl-4 ml-2">
                      <p className="text-xs font-medium text-[#9B9590] uppercase tracking-wide">Invoiced (Halaxy)</p>
                      <p className="text-2xl font-bold text-[#D4A574]" data-testid="metric-total-invoiced">
                        {formatCurrency(metrics.filtered_invoiced !== null && metrics.filtered_invoiced !== undefined ? metrics.filtered_invoiced : metrics.total_invoiced)}
                      </p>
                    </div>
                    <div className="border-l border-[#E8E4DC] pl-4 ml-2">
                      <p className="text-xs font-medium text-[#9B9590] uppercase tracking-wide">Collected</p>
                      <p className="text-2xl font-bold text-[#7BA05B]" data-testid="metric-total-paid">
                        {formatCurrency(metrics.filtered_paid !== null && metrics.filtered_paid !== undefined ? metrics.filtered_paid : metrics.total_paid_sum)}
                      </p>
                    </div>
                    <div className="border-l border-[#E8E4DC] pl-4 ml-2">
                      <p className="text-xs font-medium text-[#9B9590] uppercase tracking-wide">Clients</p>
                      <p className="text-2xl font-bold text-[#3A3530]" data-testid="metric-total-clients">
                        {metrics.filtered_count !== null && metrics.filtered_count !== undefined ? metrics.filtered_count : metrics.total_clients}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-[#9B9590]" />
                    <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                      <SelectTrigger className="w-[160px] border-[#E8E4DC] text-sm" data-testid="period-selector">
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="this_week">This Week</SelectItem>
                        <SelectItem value="this_month">This Month</SelectItem>
                        <SelectItem value="last_month">Last Month</SelectItem>
                        <SelectItem value="this_quarter">This Quarter</SelectItem>
                        <SelectItem value="this_year">This Year</SelectItem>
                        <SelectItem value="last_year">Last Year</SelectItem>
                        <SelectItem value="all_time">All Time</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Mobile: show all metrics below */}
                <div className="flex sm:hidden flex-wrap gap-4 mt-4 pt-3 border-t border-[#E8E4DC]">
                  <div>
                    <p className="text-xs font-medium text-[#9B9590]">Invoiced</p>
                    <p className="text-lg font-bold text-[#D4A574]">
                      {formatCurrency(metrics.filtered_invoiced !== null && metrics.filtered_invoiced !== undefined ? metrics.filtered_invoiced : metrics.total_invoiced)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[#9B9590]">Collected</p>
                    <p className="text-lg font-bold text-[#7BA05B]">
                      {formatCurrency(metrics.filtered_paid !== null && metrics.filtered_paid !== undefined ? metrics.filtered_paid : metrics.total_paid_sum)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-[#9B9590]">Clients</p>
                    <p className="text-lg font-bold text-[#3A3530]">
                      {metrics.filtered_count !== null && metrics.filtered_count !== undefined ? metrics.filtered_count : metrics.total_clients}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Standard KPI cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
              <Card data-testid="new-metric-card-active" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <Users className="mr-2 h-4 w-4 text-[#8B9D83]" />
                    Active Assessments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">{metrics.total_active}</p>
                </CardContent>
              </Card>

              <Card data-testid="new-metric-card-payments" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <DollarSign className="mr-2 h-4 w-4 text-[#D4A574]" />
                    Outstanding
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">
                    {formatCurrency(metrics.outstanding_payments)}
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="new-metric-card-unpaid" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <DollarSign className="mr-2 h-4 w-4 text-[#C17B6B]" />
                    To Be Paid
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">{metrics.unpaid_count}</p>
                  <p className="text-xs text-[#9B9590] mt-1">clients</p>
                </CardContent>
              </Card>

              <Card data-testid="new-metric-card-due" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <Calendar className="mr-2 h-4 w-4 text-[#8B9D83]" />
                    Due This Week
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">{metrics.due_this_week}</p>
                </CardContent>
              </Card>

              <Card data-testid="new-metric-card-month" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4 text-[#7BA05B]" />
                    This Month
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">{metrics.completed_this_month}</p>
                </CardContent>
              </Card>

              <Card data-testid="new-metric-card-completed" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <CheckCircle className="mr-2 h-4 w-4 text-[#7BA05B]" />
                    Total Completed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-[#3A3530]">{metrics.completed_all_time}</p>
                </CardContent>
              </Card>

              <Card data-testid="new-metric-card-stages" className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-[#6B6560] flex items-center">
                    <TrendingUp className="mr-2 h-4 w-4 text-[#8B9D83]" />
                    By Stage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-xs">
                    {metrics.by_stage && Object.entries(metrics.by_stage).slice(0, 3).map(([stage, count]) => (
                      <div key={stage} className="flex justify-between">
                        <span className="text-[#6B6560] truncate">{stage}</span>
                        <span className="font-semibold text-[#3A3530]">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Tools Section */}
        <div className="mb-6 flex gap-3 flex-wrap">
          <Button
            onClick={downloadContacts}
            variant="outline"
            size="sm"
            className="border-[#8B9D83] text-[#8B9D83] hover:bg-[#8B9D83]/10"
            data-testid="download-contacts-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            Download All Contacts (CSV)
          </Button>
          <Button
            onClick={runAudit}
            variant="outline"
            size="sm"
            className="border-[#E8E4DC] text-[#6B6560] hover:bg-[#F5F1E8]"
            data-testid="run-audit-btn"
          >
            <TrendingUp className="mr-2 h-4 w-4" />
            {showAudit ? 'Refresh Audit' : 'Run Data Audit'}
          </Button>
        </div>

        {showAudit && auditData && (
            <div className="mt-4 space-y-4">
              {/* Summary */}
              <Card className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#3A3530]">Audit Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-[#6B6560]">Total Clients</p>
                      <p className="font-bold text-lg">{auditData.summary.total_clients}</p>
                    </div>
                    <div>
                      <p className="text-[#6B6560]">Gross (all)</p>
                      <p className="font-bold text-lg">{formatCurrency(auditData.summary.total_gross_all)}</p>
                    </div>
                    <div>
                      <p className="text-[#6B6560]">Gross (excl disengaged)</p>
                      <p className="font-bold text-lg text-[#7BA05B]">{formatCurrency(auditData.summary.total_gross_excl_disengaged)}</p>
                    </div>
                    <div>
                      <p className="text-[#6B6560]">Duplicate Inflation</p>
                      <p className="font-bold text-lg text-[#C17B6B]">{formatCurrency(auditData.summary.potential_duplicate_inflation)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Duplicates */}
              {(auditData.name_duplicates.length > 0 || auditData.email_duplicates.length > 0) && (
                <Card className="bg-white border-[#C17B6B]/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold text-[#C17B6B]">
                      Duplicates Found ({auditData.name_duplicates.length} name, {auditData.email_duplicates.length} email)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm space-y-3">
                    {auditData.name_duplicates.map((dup, i) => (
                      <div key={i} className="border-b border-[#E8E4DC] pb-2">
                        <p className="font-medium">"{dup.name}" appears {dup.count}x:</p>
                        {dup.entries.map((e, j) => (
                          <p key={j} className="text-[#6B6560] ml-4">
                            Cost: {formatCurrency(e.total_cost)} | Status: {e.process} | Created: {e.created_at.split('.')[0]}
                          </p>
                        ))}
                      </div>
                    ))}
                    {auditData.email_duplicates.map((dup, i) => (
                      <div key={`e${i}`} className="border-b border-[#E8E4DC] pb-2">
                        <p className="font-medium">Email "{dup.email}" appears {dup.count}x:</p>
                        {dup.entries.map((e, j) => (
                          <p key={j} className="text-[#6B6560] ml-4">
                            {e.name} | Cost: {formatCurrency(e.total_cost)} | Status: {e.process} | Created: {e.created_at.split('.')[0]}
                          </p>
                        ))}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Batch Import Groups */}
              <Card className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#3A3530]">Import Batches (by created date)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-sm">
                    {Object.entries(auditData.created_at_groups).map(([date, info]) => (
                      <div key={date} className="flex justify-between items-center py-1 border-b border-[#F5F1E8]">
                        <span className="text-[#6B6560]">{date}</span>
                        <span className="font-medium">{info.count} clients — {formatCurrency(info.total_cost_sum)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Status Breakdown */}
              <Card className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#3A3530]">Breakdown by Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1 text-sm">
                    {Object.entries(auditData.status_breakdown).map(([status, info]) => (
                      <div key={status} className="flex justify-between items-center py-1 border-b border-[#F5F1E8]">
                        <span className="text-[#6B6560]">{status}</span>
                        <span className="font-medium">
                          {info.count} clients | Gross: {formatCurrency(info.total_cost_sum)} | Paid: {formatCurrency(info.total_paid_sum)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Top 20 Clients by Cost */}
              <Card className="bg-white border-[#E8E4DC]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-[#3A3530]">Top 20 Clients by Cost</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#E8E4DC]">
                          <th className="text-left py-1 pr-4 text-[#6B6560]">Name</th>
                          <th className="text-right py-1 pr-4 text-[#6B6560]">Cost</th>
                          <th className="text-right py-1 pr-4 text-[#6B6560]">Paid</th>
                          <th className="text-left py-1 pr-4 text-[#6B6560]">Status</th>
                          <th className="text-left py-1 text-[#6B6560]">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditData.top_20_by_cost.map((c, i) => (
                          <tr key={i} className="border-b border-[#F5F1E8]">
                            <td className="py-1 pr-4 font-medium">{c.name}</td>
                            <td className="py-1 pr-4 text-right">{formatCurrency(c.total_cost)}</td>
                            <td className="py-1 pr-4 text-right text-[#7BA05B]">{formatCurrency(c.total_paid)}</td>
                            <td className="py-1 pr-4">{c.process}</td>
                            <td className="py-1 text-[#9B9590]">{c.created_at.split('.')[0]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <Button
                onClick={() => setShowAudit(false)}
                variant="ghost"
                size="sm"
                className="text-[#9B9590]"
              >
                Hide Audit
              </Button>
            </div>
          )}


        {/* Active Assessments Table */}
        <div className="bg-white rounded-xl border border-[#E8E4DC] shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-[#E8E4DC] flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[#3A3530]">Active Assessments ({activeAssessments.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="new-assessments-table">
              <thead className="bg-[#F5F1E8] border-b border-[#E8E4DC]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Age</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Assessment</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#3A3530]">MHCP</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Process</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Next Session</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Total</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#3A3530]">Paid?</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Last Payment</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Halaxy</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#3A3530]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E4DC]">
                {activeAssessments.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-12 text-center text-[#9B9590]">
                      No active assessments. Add new clients from the{' '}
                      <a href="/admin/dashboard" className="text-[#8B9D83] underline hover:text-[#6B7D63] font-medium">Assessments</a>{' '}
                      page, then sync Halaxy to populate data.
                    </td>
                  </tr>
                ) : (
                  activeAssessments.map((assessment) => (
                    <tr key={assessment.id} className="hover:bg-[#FDFBF7] transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-[#3A3530]">
                        {assessment.name}
                        {planBadges[assessment.halaxy_patient_id] && (
                          <a href="/admin/payment-plans" className="ml-2 inline-block align-middle" data-testid={`plan-badge-${assessment.id}`}>
                            <StatusBadge status={planBadges[assessment.halaxy_patient_id]} testId={`plan-badge-status-${assessment.id}`} />
                          </a>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#6B6560]">{assessment.age}</td>
                      <td className="px-6 py-4 text-sm text-[#6B6560]">{assessment.assessment_type}</td>
                      <td className="px-6 py-4 text-center">
                        <Badge className={`rounded-full px-3 py-1 ${assessment.mhcp === 'Yes' ? 'bg-[#8B9D83]/20 text-[#5A6F52] border border-[#8B9D83]/30' : 'bg-[#E8E4DC]/50 text-[#9B9590] border border-[#E8E4DC]'}`}>
                          {assessment.mhcp || 'No'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={`${getStatusBadgeColor(assessment.process)} rounded-full px-3 py-1`}>
                          {assessment.process}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#6B6560]">
                        {assessment.halaxy_next_appointment ? (
                          <div>
                            <div className="font-medium text-[#3A3530]">
                              {new Date(assessment.halaxy_next_appointment).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Brisbane' })}
                            </div>
                            <div className="text-[10px] text-[#8B9D83]">via Halaxy</div>
                          </div>
                        ) : (
                          formatDate(assessment.date_of_next_session)
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-[#3A3530]">{formatCurrency(assessment.total_cost)}</td>
                      <td className="px-6 py-4 text-center">
                        {assessment.halaxy_matched && assessment.halaxy_total_invoiced > 0 ? (
                          <div className="flex flex-col items-center">
                            {getPaymentStatusIcon(assessment)}
                            <div className="text-[10px] text-[#8B9D83] mt-0.5">
                              {formatCurrency(assessment.total_paid)} paid
                            </div>
                          </div>
                        ) : (
                          getPaymentStatusIcon(assessment)
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#6B6560]">
                        {assessment.last_payment_date ? (
                          <div>
                            <div className="font-medium text-[#3A3530]">
                              {new Date(assessment.last_payment_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Australia/Brisbane' })}
                            </div>
                            <div className="text-[10px] text-[#9B9590]">
                              {Math.floor((new Date() - new Date(assessment.last_payment_date)) / (1000 * 60 * 60 * 24))} days ago
                            </div>
                          </div>
                        ) : (
                          <span className="text-[#9B9590]">No payment yet</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {assessment.halaxy_matched ? (
                          <div className="space-y-1">
                            <Badge className="rounded-full px-2 py-0.5 bg-[#D4E4CE] text-[#3A5A2E] border border-[#8B9D83]/30 text-xs">
                              Matched
                            </Badge>
                            {assessment.halaxy_balance_owing > 0 ? (
                              <div className="text-xs text-[#C17B6B] font-semibold">
                                Owing: {formatCurrency(assessment.halaxy_balance_owing)}
                              </div>
                            ) : assessment.halaxy_total_invoiced > 0 ? (
                              <div className="text-xs text-[#7BA05B] font-medium">
                                Paid in full
                              </div>
                            ) : (
                              <div className="text-xs text-[#9B9590]">
                                No invoices
                              </div>
                            )}
                            {assessment.halaxy_next_appointment && (
                              <div className="text-xs text-[#6B6560]">
                                Next: {new Date(assessment.halaxy_next_appointment).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', timeZone: 'Australia/Brisbane' })}
                              </div>
                            )}
                            {assessment.halaxy_appointment_count > 0 && (
                              <div className="text-[10px] text-[#9B9590]">
                                {assessment.halaxy_appointment_count} appt{assessment.halaxy_appointment_count !== 1 ? 's' : ''}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Badge className="rounded-full px-2 py-1 bg-[#F5F1E8] text-[#9B9590] border border-[#E8E4DC] text-xs">
                            {halaxyConnected ? 'Not found' : 'Pending'}
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            onClick={() => handleOpenDialog(assessment)}
                            variant="ghost"
                            size="sm"
                            className="text-[#8B9D83] hover:bg-[#F5F1E8]"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(assessment.id)}
                            variant="ghost"
                            size="sm"
                            className="text-[#C17B6B] hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Completed Section */}
        {completedAssessments.length > 0 && (
          <div className="bg-white rounded-xl border border-green-200 shadow-sm overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-green-200 bg-green-50/50">
              <h2 className="text-lg font-semibold text-[#3A3530]">Completed ({completedAssessments.length})</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-green-100/50 border-b border-green-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Assessment</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Total</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-[#3A3530]">Paid?</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#3A3530]">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-green-100">
                  {completedAssessments.map((a) => (
                    <tr key={a.id} className="hover:bg-green-50/30">
                      <td className="px-6 py-4 text-sm font-medium text-[#3A3530]">{a.name}</td>
                      <td className="px-6 py-4 text-sm text-[#6B6560]">{a.assessment_type}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-[#3A3530]">{formatCurrency(a.total_cost)}</td>
                      <td className="px-6 py-4 text-center">{getPaymentStatusIcon(a)}</td>
                      <td className="px-6 py-4 text-sm text-[#6B6560] max-w-xs truncate">{a.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#3A3530]">
              Edit Client
            </DialogTitle>
            <DialogDescription className="text-[#6B6560]">
              Update the client assessment details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input id="name" data-testid="new-form-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="border-[#E8E4DC] focus:border-[#8B9D83]" />
              </div>
              <div className="space-y-2">
                <Label>Age *</Label>
                <Select value={formData.age} onValueChange={(value) => setFormData({ ...formData, age: value })}>
                  <SelectTrigger className="border-[#E8E4DC]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Minor">Minor</SelectItem>
                    <SelectItem value="Adult">Adult</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" data-testid="new-form-email-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="border-[#E8E4DC] focus:border-[#8B9D83]" />
              </div>
              <div className="space-y-2">
                <Label>Assessment Type *</Label>
                <Select value={formData.assessment_type} onValueChange={(value) => setFormData({ ...formData, assessment_type: value })}>
                  <SelectTrigger className="border-[#E8E4DC]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ASD">ASD</SelectItem>
                    <SelectItem value="ADHD">ADHD</SelectItem>
                    <SelectItem value="Combination (ASD + ADHD)">Combination (ASD + ADHD)</SelectItem>
                    <SelectItem value="ASD & Dyslexia">ASD & Dyslexia</SelectItem>
                    <SelectItem value="ADHD & Dyslexia">ADHD & Dyslexia</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Intake Paperwork</Label>
                <Select value={formData.intake_paperwork} onValueChange={(value) => setFormData({ ...formData, intake_paperwork: value })}>
                  <SelectTrigger className="border-[#E8E4DC]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>MHCP</Label>
                <Select value={formData.mhcp} onValueChange={(value) => setFormData({ ...formData, mhcp: value })}>
                  <SelectTrigger className="border-[#E8E4DC]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Type *</Label>
                <Select value={formData.payment_type} onValueChange={(value) => setFormData({ ...formData, payment_type: value })}>
                  <SelectTrigger className="border-[#E8E4DC]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NDIS">NDIS</SelectItem>
                    <SelectItem value="Private">Private</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Total Cost (AUD) *</Label>
                <Input type="number" step="0.01" data-testid="new-form-cost-input" value={formData.total_cost} onChange={(e) => setFormData({ ...formData, total_cost: e.target.value })} required className="border-[#E8E4DC] focus:border-[#8B9D83]" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Paid (AUD)</Label>
                <Input type="number" step="0.01" data-testid="new-form-paid-input" value={formData.total_paid} onChange={(e) => setFormData({ ...formData, total_paid: e.target.value })} className="border-[#E8E4DC] focus:border-[#8B9D83]" />
              </div>
              <div className="space-y-2">
                <Label>Process Stage *</Label>
                <Select value={formData.process} onValueChange={(value) => setFormData({ ...formData, process: value })}>
                  <SelectTrigger className="border-[#E8E4DC]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Intake Sent">Intake Sent</SelectItem>
                    <SelectItem value="Intake Received">Intake Received</SelectItem>
                    <SelectItem value="Background Booked">Background Booked</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Report Writing">Report Writing</SelectItem>
                    <SelectItem value="Report Completed">Report Completed</SelectItem>
                    <SelectItem value="Feedback Session">Feedback Session</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Disengaged">Disengaged</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Date of Next Session</Label>
              <Input type="date" value={formData.date_of_next_session} onChange={(e) => setFormData({ ...formData, date_of_next_session: e.target.value })} disabled={dateNotSet} className="border-[#E8E4DC] focus:border-[#8B9D83] disabled:opacity-50" />
              <div className="flex items-center space-x-2">
                <Checkbox id="dateNotSet" checked={dateNotSet} onCheckedChange={(checked) => { setDateNotSet(checked); if (checked) setFormData({ ...formData, date_of_next_session: '' }); }} />
                <Label htmlFor="dateNotSet" className="text-sm text-[#6B6560]">Date not set</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea data-testid="new-form-notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Add any additional notes..." className="border-[#E8E4DC] focus:border-[#8B9D83]" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#E8E4DC]">Cancel</Button>
              <Button type="submit" disabled={isSubmitting} data-testid="new-form-submit-btn" className="bg-[#8B9D83] hover:bg-[#6B7D63] text-white disabled:opacity-50">
                {isSubmitting ? 'Saving...' : 'Update Assessment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
